import TYPES from '@/types';
import Vue from 'vue';
import { postalCodeFormat, requiredRule, requiredRuleByKey } from '@/vue-app/utils/form-rules';

// Application
import {
  SearchSettlementsQuery,
} from '@/modules/my-investment/catalogs/allianz/settlement/application/queries';
import {
  SearchMunicipalitiesQuery,
} from '@/modules/my-investment/catalogs/allianz/municipality/application/queries';
import {
  SearchStatesQuery,
} from '@/modules/my-investment/catalogs/allianz/state/application/queries';
import {
  SearchCountriesQuery,
} from '@/modules/my-investment/catalogs/allianz/country/application/queries';
import AddressService
  from '@/modules/user/customer-address/application/services/address-service';
import ChooseYourDocumentsService
  from '@/modules/user/customer-address/application/services/choose-your-documents-service';

// Domain
import {
  SearchDto,
} from '@/modules/my-investment/catalogs/allianz/municipality/domain/dtos/search-dto';
import {
  SettlementEntity,
} from '@/modules/my-investment/catalogs/allianz/settlement/domain/entities/settlement-entity';
import {
  MunicipalityEntity,
} from '@/modules/my-investment/catalogs/allianz/municipality/domain/entities/municipality-entity';
import {
  StateEntity,
} from '@/modules/my-investment/catalogs/allianz/state/domain/entities/state-entity';
import {
  CountryEntity,
} from '@/modules/my-investment/catalogs/allianz/country/domain/entities/country-entity';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

type Address = {
  street: string;
  external_number: string;
  interior_number: string;
  postal_code: string;
  settlement: {
    codPais: string;
    descPais: string;
    codEstado: string;
    descEstado: string;
    codLocalidad: string;
    codMunicipio: string;
    codigoPostal: string;
    descLocalidad: string;
    descMunicipio: string;
  };
  municipality: {
    codPais: string;
    codEstado: string;
    codMunicipio: string;
    descMunicipio: string;
  };
  state: {
    codestado: string;
    descestado: string;
    codpais: string;
  };
  country: {
    descpais: string;
    codpais: string;
  };
}

export default class UpdateProofOfAddressDataViewModel {
  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.SEARCH_CATALOG_ALLIANZ_SETTLEMENT_QUERY)
  readonly search_settlements_query!: SearchSettlementsQuery;

  @Inject(TYPES.SEARCH_CATALOG_ALLIANZ_MUNICIPALITY_QUERY)
  readonly search_municipalities_query!: SearchMunicipalitiesQuery;

  @Inject(TYPES.SEARCH_CATALOG_ALLIANZ_STATE_QUERY)
  readonly search_states_query!: SearchStatesQuery;

  @Inject(TYPES.SEARCH_CATALOG_ALLIANZ_COUNTRY_QUERY)
  readonly search_countries_query!: SearchCountriesQuery;

  @Inject(TYPES.ADDRESS_SERVICE)
  private readonly address_service!: AddressService;

  @Inject(TYPES.CHOOSE_YOUR_DOCUMENTS_SERVICE)
  private readonly choose_your_documents_service!: ChooseYourDocumentsService;

  readonly view!: Vue;

  readonly i18n_namespace = 'components.user-profile.user-profile-tabs.user-profile-my-documents-tab.update-proof-of-address.data';

  public constructor(view: Vue) {
    this.view = view;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  is_loading = true;

  is_form_valid = false;

  small_description = this.translate('description_2');

  settlement_items: SettlementEntity[] = [];

  municipalities_items: MunicipalityEntity[] = [];

  states_items: StateEntity[] = [];

  countries_items: CountryEntity[] = [];

  address: Address = {
    street: '',
    external_number: '',
    interior_number: '',
    postal_code: '',
    settlement: {
      codPais: '',
      descPais: '',
      codEstado: '',
      descEstado: '',
      codLocalidad: '',
      codMunicipio: '',
      codigoPostal: '',
      descLocalidad: '',
      descMunicipio: '',
    },
    municipality: {
      codPais: '',
      codEstado: '',
      codMunicipio: '',
      descMunicipio: '',
    },
    state: {
      codestado: '',
      descestado: '',
      codpais: '',
    },
    country: {
      descpais: 'MÉXICO',
      codpais: '412',
    },
  };

  rules = {
    street: [requiredRule],
    external_number: [requiredRule],
    internal_number: [requiredRule],
    postal_code: [requiredRule, postalCodeFormat],
    settlement: [(value: string) => requiredRuleByKey(value, 'codLocalidad')],
    municipality: [(value: string) => requiredRuleByKey(value, 'codMunicipio')],
    state: [(value: string) => requiredRuleByKey(value, 'codestado')],
    country: [(value: string) => requiredRuleByKey(value, 'codpais')],
  };

  initialize = async () => {
    await this.loadCountries();
    await this.loadInformationFromStore();
  }

  get is_begin_btn_disabled() {
    return !this.is_form_valid;
  }

  getSettlements = async () => {
    try {
      if (this.address.postal_code.length === 5) {
        return await this.search_settlements_query.execute(this.address.postal_code);
      }
      return [];
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.search_settlements_query'));
      return [];
    }
  }

  getMunicipalities = async () => {
    try {
      if (this.address.country.codpais !== '' && this.address.state.codestado !== '') {
        const search: SearchDto = {
          codpais: this.address.country.codpais,
          codestado: this.address.state.codestado,
        };
        return await this.search_municipalities_query.execute(search);
      }
      return [];
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.search_municipalities_query'));
      return [];
    }
  }

  getStates = async () => {
    try {
      return await this.search_states_query.execute(this.address.country.codpais);
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.search_states_query'));
      return [];
    }
  }

  loadInformationFromStore = async () => {
    this.address = this.address_service.getAddressData();
    const { pocket_option_is_checked, wealth_option_is_checked } = this
      .choose_your_documents_service.getChooseYourDocumentsData();
    if (pocket_option_is_checked && wealth_option_is_checked) {
      this.small_description += `${this.translate('allianz')} y ${this.translate('kuspit')}.`;
    } else {
      this.small_description += (wealth_option_is_checked) ? `${this.translate('allianz')}.` : `${this.translate('kuspit')}.`;
    }
    await this.changePostalCode(true);
  }

  loadCountries = async () => {
    try {
      this.countries_items = await this.search_countries_query.execute();
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.search_countries_query'));
    }
  }

  changePostalCode = async (data_is_loaded_from_state: boolean) => {
    const settlements = await this.getSettlements();
    if (settlements.length > 0) {
      if (!data_is_loaded_from_state) {
        this.address.settlement = {
          codPais: '',
          descPais: '',
          codEstado: '',
          descEstado: '',
          codLocalidad: '',
          codMunicipio: '',
          codigoPostal: '',
          descLocalidad: '',
          descMunicipio: '',
        };
      }
      this.settlement_items = settlements;
      const country = this.countries_items
        .find((item) => item.codpais === this.settlement_items[0].codPais);
      if (country) {
        this.address.country = country;
        const states = await this.getStates();
        this.states_items = states;
        const state = states.find((item) => item.codestado === this.settlement_items[0].codEstado);
        if (state) {
          this.address.state = state;
          const municipalities_list = await this.getMunicipalities();
          this.municipalities_items = municipalities_list;
          const municipality = municipalities_list
            .find((item) => item.codMunicipio === this.settlement_items[0].codMunicipio);
          if (municipality) {
            this.address.municipality = municipality;
          }
        }
      }
    }
  }

  setInformationToStore = () => {
    this.address_service.setAddressData(this.address);
  }

  nextStep = () => {
    this.setInformationToStore();
    this.view.$emit('nextStep');
  }

  prevStep = () => {
    this.view.$emit('prevStep');
  }
}
